import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getThemeBySlug } from '@/lib/config/themes';

const ThemeContext = createContext(null);

/**
 * Résout le slug du thème selon la stratégie choisie.
 *
 * Priorité :
 * 1. Query param URL : "?client-theme=horodateme" (dev/test, persisté en localStorage)
 * 2. Injection backend via window.__THEME_SLUG__ (inclut le localStorage via app.blade.php)
 * 3. Sous-domaine : "client-a.horodaty.fr" → "client-a"
 * 4. Fallback : "default"
 */
function resolveThemeSlug() {
    // Priorité 1 : query param ?client-theme= (persisté en localStorage)
    const params = new URLSearchParams(window.location.search);
    const paramTheme = params.get('client-theme');
    if (paramTheme) {
        try {
            localStorage.setItem('client-theme', paramTheme);
        } catch (e) {}
        return paramTheme;
    }

    // Priorité 2 : injection backend / localStorage (via app.blade.php)
    if (window.__THEME_SLUG__ && window.__THEME_SLUG__ !== 'default') {
        return window.__THEME_SLUG__;
    }

    // Priorité 3 : sous-domaine
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    if (parts.length >= 3 && parts[0] !== 'www') {
        return parts[0];
    }

    return 'default';
}

/**
 * Provider de thème pour l'application.
 *
 * - Résout le thème client au montage (sous-domaine, query param, ou injection backend)
 * - Applique les overrides CSS custom properties sur <html>
 * - Pose un attribut data-theme sur <html> pour les sélecteurs CSS
 * - Fournit le thème courant et ses métadonnées (logo, tagline, etc.) via useTheme()
 *
 * Doit être placé le plus haut possible dans l'arbre (avant AuthProvider, ApiCacheProvider, etc.)
 * car il n'a aucune dépendance sur les autres contextes.
 */
export function ThemeProvider({ children }) {
    const [themeSlug, setThemeSlug] = useState(resolveThemeSlug);
    const theme = useMemo(() => getThemeBySlug(themeSlug), [themeSlug]);

    // Appliquer les CSS variables et data-attribute sur <html>
    useEffect(() => {
        const root = document.documentElement;

        // Poser l'attribut data-theme (pour les sélecteurs CSS [data-theme='...'])
        if (themeSlug !== 'default') {
            root.setAttribute('data-theme', themeSlug);
        } else {
            root.removeAttribute('data-theme');
        }

        // Appliquer les overrides CSS variables
        Object.entries(theme.colors).forEach(([prop, value]) => {
            root.style.setProperty(prop, value);
        });

        // Appliquer le favicon du thème
        let faviconLink = document.querySelector('link[rel="icon"]');
        if (!faviconLink) {
            faviconLink = document.createElement('link');
            faviconLink.rel = 'icon';
            document.head.appendChild(faviconLink);
        }
        if (theme.favicon) {
            faviconLink.href = theme.favicon;
        }

        // Cleanup : retirer les overrides au changement/démontage
        return () => {
            Object.keys(theme.colors).forEach((prop) => {
                root.style.removeProperty(prop);
            });
            root.removeAttribute('data-theme');
        };
    }, [theme, themeSlug]);

    return <ThemeContext.Provider value={{ theme, themeSlug, setThemeSlug }}>{children}</ThemeContext.Provider>;
}

/**
 * Hook pour accéder au thème courant.
 *
 * @returns {{ theme: object, themeSlug: string, setThemeSlug: (slug: string) => void }}
 *   - theme : config complète du thème (name, logo, logoAlt, tagline, colors)
 *   - themeSlug : slug actif (ex: 'default', 'client-a')
 *   - setThemeSlug : permet de changer le thème dynamiquement
 */
export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}
